import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ExternalLinkIcon } from 'lucide-react';
import {
  DataTable,
  EmptyState,
  ErrorNotice,
  LoadingState,
  PageHeading,
  Pagination,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  StatTile,
  TrendChart,
  useDebouncedValue,
  type DataTableSort,
  type PillTone,
  type TrendChartVariant,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { useTableSort } from '@/lib/use-table-sort';
import { useLocalSort } from '@/lib/use-local-sort';
import {
  CHART_VARIANTS,
  FORWARD_RANGES,
  INTERVAL_LABELS,
  LIVE_REFRESH_MS,
  RANGES,
  TREND_WINDOWS,
  forwardWindow,
  money,
  periodLabel,
  rangeWindow,
  toTrendOverlays,
  toTrendPoints,
  toTrendSeries,
  toYoyOverlay,
  type ForwardRangeKey,
  type RangeKey,
  type TrendWindowKey,
} from '@/lib/report-window';
import { TogglePill } from '@/components/report-bits';
import {
  EVENT_SEGMENT,
  OPPORTUNITY_OUTCOME,
  OPPORTUNITY_SORT_KEYS,
  listOpportunities,
  readEventStats,
  salesforceOpportunityUrl,
  type EventBreakdownRow,
  type EventSegment,
  type Opportunity,
  type OpportunityOutcome,
} from '@/lib/opportunities';
import { STATS_INTERVAL, type StatsInterval } from '@/lib/leads';
import { useIntervalChoice } from '@/lib/use-interval-choice';

/** Events per page in the table. */
const PAGE_SIZE = 25;

/** A YYYY-MM-DD window bound, as the P&L drill-in and the wire use it. */
const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Soonest event first, unless the URL says otherwise. */
const DEFAULT_SORT: DataTableSort = { key: 'eventDate', direction: 'asc' };

/** Forward presets first — "what's coming" is the everyday question. */
const EVENT_RANGE_KEYS: ReadonlyArray<ForwardRangeKey | RangeKey> = [
  'next30',
  'next90',
  'next12m',
  '12m',
  '24m',
  '36m',
];

function isForward(key: ForwardRangeKey | RangeKey): key is ForwardRangeKey {
  return key in FORWARD_RANGES;
}

const OUTCOME_LABELS: Readonly<Record<OpportunityOutcome, { label: string; tone: PillTone }>> = {
  signed: { label: 'Signed', tone: 'success' },
  cancelled: { label: 'Cancelled', tone: 'warning' },
  open: { label: 'Open', tone: 'info' },
  lost: { label: 'Lost', tone: 'danger' },
  unqualified: { label: 'Unqualified', tone: 'neutral' },
};

type SegmentChoice = EventSegment | 'none';
const SEGMENT_OPTIONS: readonly { key: SegmentChoice; label: string }[] = [
  { key: 'none', label: 'Total' },
  { key: EVENT_SEGMENT.program, label: 'By program' },
  { key: EVENT_SEGMENT.region, label: 'By state' },
  { key: EVENT_SEGMENT.salesperson, label: 'By salesperson' },
];

type OutcomeFilter = OpportunityOutcome | 'all';
const OUTCOME_FILTERS: readonly { key: OutcomeFilter; label: string }[] = [
  { key: OPPORTUNITY_OUTCOME.signed, label: 'Signed' },
  { key: OPPORTUNITY_OUTCOME.cancelled, label: 'Cancelled' },
  { key: 'all', label: 'All' },
];

/** The event's city and state. */
function eventPlace(deal: Pick<Opportunity, 'eventCity' | 'eventState'>): string {
  return [deal.eventCity, deal.eventState].filter(Boolean).join(', ') || '—';
}

/**
 * The event calendar (admin-only): signed events by EVENT date — what is
 * coming and what ran, revenue by event month, program and state mix, how
 * far ahead events are booked, and cancellations. Over the opportunity
 * mirror, refreshed every five minutes.
 *
 * @returns The events analytics page.
 */
export function EventsAnalyticsPage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const [range, setRange] = useState<ForwardRangeKey | RangeKey>('next90');
  const [variant, setVariant] = useState<TrendChartVariant>('bar');
  const [trends, setTrends] = useState<ReadonlySet<TrendWindowKey>>(() => new Set());
  const [segment, setSegment] = useState<SegmentChoice>('none');
  const [yoy, setYoy] = useState(false);
  // An explicit [from, to) window in the URL — e.g. drilling in from a month's
  // revenue on the P&L — overrides the preset range and shows as "Custom".
  const fromParam = params.get('from');
  const toParam = params.get('to');
  const isCustomWindow = Boolean(fromParam && toParam && YMD_RE.test(fromParam) && YMD_RE.test(toParam));
  const statsWindow = useMemo(
    () =>
      isCustomWindow
        ? { from: fromParam as string, to: toParam as string }
        : isForward(range)
          ? forwardWindow(range)
          : rangeWindow(range),
    [isCustomWindow, fromParam, toParam, range],
  );
  const [effectiveInterval, setIntervalChoice] = useIntervalChoice(
    isCustomWindow
      ? STATS_INTERVAL.day
      : isForward(range)
        ? FORWARD_RANGES[range].interval
        : RANGES[range].interval,
  );
  const leadIn =
    trends.size === 0
      ? 0
      : Math.max(...TREND_WINDOWS[effectiveInterval].map((window) => window.buckets)) - 1;

  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);
  const urlSearch = params.get('q') ?? '';
  const [search, setSearch] = useState(urlSearch);
  const debounced = useDebouncedValue(search.trim());
  useEffect(() => {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (debounced) next.set('q', debounced);
        else next.delete('q');
        if (debounced !== urlSearch) next.delete('page');
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, setParams]);
  const setPage = (next: number): void => {
    setParams((previous) => {
      const nextParams = new URLSearchParams(previous);
      nextParams.set('page', String(next));
      return nextParams;
    });
  };
  const outcomeParam = params.get('outcome');
  const outcomeFilter: OutcomeFilter =
    outcomeParam !== null && OUTCOME_FILTERS.some((option) => option.key === outcomeParam)
      ? (outcomeParam as OutcomeFilter)
      : OPPORTUNITY_OUTCOME.signed;
  const setOutcome = (next: OutcomeFilter): void => {
    setParams(
      (previous) => {
        const nextParams = new URLSearchParams(previous);
        nextParams.set('outcome', next);
        nextParams.delete('page');
        return nextParams;
      },
      { replace: true },
    );
  };
  // Which date the events table filters by — event date (default) or signed
  // date. The P&L revenue drill-in passes this so the list matches the basis.
  const dateBy: 'signed' | 'event' = params.get('dateBy') === 'signed' ? 'signed' : 'event';
  const { sort, onSort } = useTableSort(OPPORTUNITY_SORT_KEYS, DEFAULT_SORT);

  const stats = useQuery({
    queryKey: ['event-stats', statsWindow.from, statsWindow.to, effectiveInterval, leadIn, segment, yoy],
    queryFn: ({ signal }) =>
      readEventStats(
        {
          from: statsWindow.from,
          to: statsWindow.to,
          interval: effectiveInterval,
          leadIn,
          ...(segment !== 'none' ? { segment } : {}),
          ...(yoy ? { yoy: '1' as const } : {}),
        },
        signal,
      ),
    refetchInterval: LIVE_REFRESH_MS,
    placeholderData: keepPreviousData,
  });
  const events = useQuery({
    queryKey: ['events-table', statsWindow.from, statsWindow.to, dateBy, debounced, outcomeFilter, page, sort],
    queryFn: ({ signal }) =>
      listOpportunities(
        {
          from: statsWindow.from,
          to: statsWindow.to,
          dateBy,
          page,
          limit: PAGE_SIZE,
          ...(debounced ? { search: debounced } : {}),
          ...(outcomeFilter !== 'all' ? { outcome: outcomeFilter } : {}),
          ...(sort ? { sort: sort.key, dir: sort.direction } : {}),
        },
        signal,
      ),
    refetchInterval: LIVE_REFRESH_MS,
    placeholderData: keepPreviousData,
  });

  if (stats.isPending) return <LoadingState label="Loading events…" />;
  if (stats.isError) {
    return <ErrorNotice title="Events unavailable" message={errorMessage(stats.error)} />;
  }

  const data = stats.data;
  const totals = data.totals;

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="ANALYTICS"
        title="Events"
        subtitle="Signed events on the calendar, by event date — what is coming, what it is worth, and how far ahead it was booked."
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <SegmentedControl<ForwardRangeKey | RangeKey | 'custom'>
          label="Range"
          value={isCustomWindow ? 'custom' : range}
          onChange={(next) => {
            if (next === 'custom') return;
            if (isCustomWindow) {
              setParams(
                (previous) => {
                  const nextParams = new URLSearchParams(previous);
                  nextParams.delete('from');
                  nextParams.delete('to');
                  nextParams.delete('page');
                  return nextParams;
                },
                { replace: true },
              );
            }
            setRange(next);
          }}
          options={[
            ...(isCustomWindow ? [{ key: 'custom' as const, label: 'Custom' }] : []),
            ...EVENT_RANGE_KEYS.map((key) => ({
              key,
              label: isForward(key) ? FORWARD_RANGES[key].label : `Past ${RANGES[key].label}`,
            })),
          ]}
        />
        <SegmentedControl<StatsInterval>
          label="Interval"
          value={effectiveInterval}
          onChange={setIntervalChoice}
          options={(Object.keys(INTERVAL_LABELS) as StatsInterval[]).map((key) => ({
            key,
            label: INTERVAL_LABELS[key],
          }))}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
        <StatTile
          label="Events"
          value={totals.events.toLocaleString()}
          changePct={totals.changePct}
          hint={`vs ${totals.previousEvents.toLocaleString()} the previous period`}
        />
        <StatTile
          label="Revenue"
          value={money.format(totals.value)}
          hint="Contract totals, by event date"
        />
        <StatTile
          label="Participants"
          value={totals.participants.toLocaleString()}
          hint={`${totals.averageParticipants.toLocaleString()} per event on average`}
        />
        <StatTile
          label="Booked ahead"
          value={totals.medianLeadTimeDays === null ? '—' : `${totals.medianLeadTimeDays} days`}
          hint="Median from signature to event"
        />
        <StatTile
          label="Cancelled"
          value={totals.cancelled.toLocaleString()}
          hint={`${money.format(totals.cancelledValue)} of signed work canceled`}
        />
      </div>

      <Panel className="mt-4 p-5">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-portal-card-title font-bold text-portal-ink">Events on the calendar</h2>
            <p className="text-portal-micro text-portal-muted">
              {INTERVAL_LABELS[effectiveInterval]} · {periodLabel(data.range.from, STATS_INTERVAL.day)} –{' '}
              {periodLabel(data.range.to, STATS_INTERVAL.day)} (by event date)
            </p>
          </div>
          {/* min-w-0 lets the control group shrink below its natural width on a
              phone so each SegmentedControl scrolls internally instead of pushing
              the page wider than the screen. */}
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <SegmentedControl<SegmentChoice>
              label="Split by"
              value={segment}
              onChange={setSegment}
              options={SEGMENT_OPTIONS.map((option) => ({ key: option.key, label: option.label }))}
            />
            <SegmentedControl<TrendChartVariant>
              label="Chart type"
              value={variant}
              onChange={setVariant}
              options={CHART_VARIANTS.map((option) => ({ key: option.key, label: option.label }))}
            />
            <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Trend lines">
              {TREND_WINDOWS[effectiveInterval].map((window) => (
                <TogglePill
                  key={window.key}
                  on={trends.has(window.key)}
                  onToggle={() =>
                    setTrends((previous) => {
                      const next = new Set(previous);
                      if (next.has(window.key)) next.delete(window.key);
                      else next.add(window.key);
                      return next;
                    })
                  }
                >
                  {window.label}
                </TogglePill>
              ))}
              <TogglePill on={yoy} onToggle={() => setYoy((previous) => !previous)}>
                Last year
              </TogglePill>
            </div>
          </div>
        </div>
        {totals.events === 0 ? (
          <EmptyState title="No events in this range" message="Try a wider range." />
        ) : (
          <TrendChart
            points={toTrendPoints(data, (point) => point.events, (point) =>
              point.events > 0
                ? [money.format(point.value), `${point.participants.toLocaleString()} participants`]
                : [],
            )}
            series={toTrendSeries(data)}
            overlays={[
              ...toTrendOverlays(data, data.series.map((point) => point.events), trends),
              ...(toYoyOverlay(data) ? [toYoyOverlay(data)!] : []),
            ]}
            variant={variant}
            title="Events"
            unit="events"
          />
        )}
      </Panel>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <EventBreakdownTable title="Program performance" searchLabel="Search programs" rows={data.byProgram} />
        <EventBreakdownTable title="By state" rows={data.byState} />
        <Panel className="p-5">
          <h3 className="text-portal-card-title font-bold text-portal-ink">How far ahead events are booked</h3>
          <p className="text-portal-micro text-portal-muted">Signature-to-event lead time.</p>
          {totals.events === 0 ? (
            <p className="mt-2 text-portal-micro text-portal-muted">Nothing in this range.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {data.leadTimes.map((bucket) => {
                const max = Math.max(1, ...data.leadTimes.map((entry) => entry.events));
                return (
                  <li key={bucket.key} className="text-portal-small">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-portal-ink">{bucket.key}</span>
                      <span className="text-portal-muted tabular-nums">{bucket.events.toLocaleString()}</span>
                    </div>
                    <div className="mt-1 h-1.5 w-full rounded-full bg-portal-bg" aria-hidden="true">
                      <div
                        className="h-1.5 rounded-full bg-brand"
                        style={{ width: `${Math.round((bucket.events / max) * 100)}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>
      </div>

      <div className="mt-8 mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-portal-section font-bold text-portal-ink">Events in this range</h2>
          <p className="text-portal-micro text-portal-muted">
            Every event dated in the range — search, sort, or narrow by outcome.
          </p>
        </div>
        {/* The search floats centered between the heading and the outcome tabs. */}
        <div className="flex flex-1 justify-center px-4">
          <SearchInput
            label="Search events"
            placeholder="Account, program, salesperson, city, invoice…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full max-w-80 min-w-56"
          />
        </div>
        <SegmentedControl<OutcomeFilter>
          label="Outcome"
          value={outcomeFilter}
          onChange={setOutcome}
          options={OUTCOME_FILTERS.map((option) => ({ key: option.key, label: option.label }))}
        />
      </div>
      {events.isError ? (
        <ErrorNotice title="Events list unavailable" message={errorMessage(events.error)} />
      ) : events.isPending ? (
        <LoadingState label="Loading events…" />
      ) : events.data.items.length === 0 ? (
        <Panel>
          <EmptyState
            title={debounced ? 'No events match' : 'No events in this range'}
            message={debounced ? 'Try a different search.' : 'Events appear here as the sync brings them in.'}
          />
        </Panel>
      ) : (
        <>
          <DataTable
            headers={[
              { label: 'Event date', sortKey: 'eventDate' },
              { label: 'Account', sortKey: 'account' },
              'Program',
              'Where',
              'Participants',
              { label: 'Salesperson', sortKey: 'salesperson' },
              { label: 'Stage', sortKey: 'stage' },
              { label: 'Value', sortKey: 'value' },
              'Salesforce',
            ]}
            visuallyHiddenHeaders={[8]}
            rightAlignedHeaders={[4, 7]}
            sort={sort}
            onSort={onSort}
            minWidth={1000}
            cards={events.data.items.map((deal) => ({
              key: deal.id,
              title: deal.accountName || '—',
              ...(deal.programName ? { subtitle: deal.programName } : {}),
              fields: [
                { label: 'Event date', value: deal.eventDate ? periodLabel(deal.eventDate.slice(0, 10), STATS_INTERVAL.day) : '—' },
                { label: 'Value', value: deal.totalCost === null ? '—' : money.format(deal.totalCost) },
                { label: 'Where', value: eventPlace(deal) },
                { label: 'Participants', value: deal.participants ?? '—' },
                { label: 'Salesperson', value: deal.salesperson || '—' },
                { label: 'Stage', value: <Pill tone={OUTCOME_LABELS[deal.outcome].tone}>{OUTCOME_LABELS[deal.outcome].label}</Pill> },
              ],
              action: (
                <a
                  href={salesforceOpportunityUrl(deal.salesforceId)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-10 items-center gap-1 text-portal-small text-brand"
                >
                  <ExternalLinkIcon className="size-4" aria-hidden="true" /> Open in Salesforce
                </a>
              ),
            }))}
          >
            {events.data.items.map((deal) => (
              <tr key={deal.id} className="border-t border-portal-line align-middle">
                <td className="p-3 whitespace-nowrap text-portal-small text-portal-muted">
                  {deal.eventDate ? periodLabel(deal.eventDate.slice(0, 10), STATS_INTERVAL.day) : '—'}
                </td>
                <td className="p-3 text-portal-small">
                  <span className="block font-bold text-portal-ink">{deal.accountName || '—'}</span>
                  {deal.invoiceNumber ? (
                    <span className="block font-mono text-portal-micro text-portal-muted">{deal.invoiceNumber}</span>
                  ) : null}
                </td>
                <td className="p-3 text-portal-small text-portal-muted">{deal.programName || '—'}</td>
                <td className="p-3 text-portal-small text-portal-muted">{eventPlace(deal)}</td>
                <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">
                  {deal.participants ?? '—'}
                </td>
                <td className="p-3 text-portal-small text-portal-muted">{deal.salesperson || '—'}</td>
                <td className="p-3 text-portal-small">
                  <Pill tone={OUTCOME_LABELS[deal.outcome].tone}>{OUTCOME_LABELS[deal.outcome].label}</Pill>
                </td>
                <td className="p-3 text-right text-portal-small tabular-nums">
                  {deal.totalCost === null ? '—' : money.format(deal.totalCost)}
                </td>
                <td className="p-3 text-right">
                  <a
                    href={salesforceOpportunityUrl(deal.salesforceId)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-brand"
                    aria-label={`Open ${deal.accountName || deal.name} in Salesforce`}
                  >
                    <ExternalLinkIcon className="size-4" aria-hidden="true" />
                  </a>
                </td>
              </tr>
            ))}
          </DataTable>
          <Pagination
            page={events.data.pagination.page}
            totalPages={events.data.pagination.totalPages}
            onChange={setPage}
            summary={`${(events.data.pagination.page - 1) * PAGE_SIZE + 1}–${
              (events.data.pagination.page - 1) * PAGE_SIZE + events.data.items.length
            } of ${events.data.pagination.total.toLocaleString()}`}
            className="mt-3"
          />
        </>
      )}
    </section>
  );
}

/** Sort accessors for the event breakdown tables — module-level so the hook memo holds. */
const EVENT_BREAKDOWN_ACCESSORS: Readonly<
  Record<string, (row: EventBreakdownRow) => number | string | null>
> = {
  key: (row) => row.key,
  events: (row) => row.events,
  participants: (row) => row.participants,
  value: (row) => row.value,
  avg: (row) => (row.events === 0 ? null : row.value / row.events),
};

/** Events, participants and value per key — sortable, optionally searchable. */
function EventBreakdownTable({
  title,
  searchLabel,
  rows,
}: {
  readonly title: string;
  /** When present, a search box filters the rows by key. */
  readonly searchLabel?: string;
  readonly rows: readonly EventBreakdownRow[];
}): React.ReactElement {
  const [query, setQuery] = useState('');
  const { sorted, sort, onSort } = useLocalSort(rows, EVENT_BREAKDOWN_ACCESSORS, {
    key: 'events',
    direction: 'desc',
  });
  const shown =
    query.trim() === ''
      ? sorted
      : sorted.filter((row) => row.key.toLowerCase().includes(query.trim().toLowerCase()));
  return (
    <Panel>
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-5 pb-4">
        <h3 className="text-portal-card-title font-bold text-portal-ink">{title}</h3>
        {searchLabel ? (
          <SearchInput
            label={searchLabel}
            placeholder="Name…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="w-full sm:w-48"
          />
        ) : null}
      </div>
      {shown.length === 0 ? (
        <p className="px-5 pb-5 text-portal-micro text-portal-muted">
          {query ? 'Nothing matches.' : 'Nothing in this range.'}
        </p>
      ) : (
        <div className="max-h-[420px] overflow-y-auto rounded-b-[8px]">
          <DataTable
            headers={[
              { label: 'Segment', sortKey: 'key' },
              { label: 'Events', sortKey: 'events' },
              { label: 'People', sortKey: 'participants' },
              { label: 'Value', sortKey: 'value' },
            ]}
            visuallyHiddenHeaders={[0]}
            rightAlignedHeaders={[1, 2, 3]}
            minWidth={320}
            flush
            sort={sort}
            onSort={onSort}
          >
            {shown.map((row) => (
              <tr key={row.key} className="border-t border-portal-line align-middle">
                <td className="p-3 text-portal-small font-bold text-portal-ink">{row.key}</td>
                <td className="p-3 text-right text-portal-small tabular-nums">{row.events.toLocaleString()}</td>
                <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">
                  {row.participants.toLocaleString()}
                </td>
                <td className="p-3 text-right text-portal-small tabular-nums">{money.format(row.value)}</td>
              </tr>
            ))}
          </DataTable>
        </div>
      )}
    </Panel>
  );
}
